import React, { useState, useEffect, useRef, useCallback } from 'react'
import Layout from '../../../components/Layout/Layout'
import axios from 'axios'
import AdminAddBook from './AddBook'
import AdminBookCategory from './BookCategory'
import AdminEditBook from './EditBook'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button, Modal } from 'react-bootstrap'

const AdminBookStockList = () => {
  const navigate = useNavigate()

  const [items, setItems] = useState([])
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [searchData, setSearchData] = useState('')
  const [hasMore, setHasMore] = useState(true)

  const [editableRow, setEditableRow] = useState(null)
  const [editedPrice, setEditedPrice] = useState('')

  const [showAddBookModal, setShowAddBookModal] = useState(false)
  const [showEditBookModal, setShowEditBookModal] = useState(false)
  const [editBookData, setEditBookData] = useState(null)
  const [showCategoryModal, setShowCategoryModal] = useState(false)

  const importFileRef = useRef(null)
  const [importing, setImporting] = useState(false)
  const [showImportGuide, setShowImportGuide] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)

  const observer = useRef(null)

  const getAllItems = async (pageToLoad = page) => {
    if (loading) return
    if (!hasMore && pageToLoad !== 1) return

    setLoading(true)
    try {
      const response = await axios.get('/api/v1/admin/bookstock/list', {
        params: { page: pageToLoad, limit: 30 },
      })

      const newItems = response.data?.items || []
      setItems((prev) => (pageToLoad === 1 ? newItems : [...prev, ...newItems]))
      setHasMore(newItems.length > 0)
      if (pageToLoad === 1) {
        setTotalRecords(response.data?.pagination?.totalItems ?? 0)
      }
    } catch (error) {
      console.error('Error fetching items:', error)
    } finally {
      setLoading(false)
    }
  }

  const searchProductResult = async (q) => {
    setLoading(true)
    try {
      const response = await axios.post('/api/v1/admin/bookstock/search', {
        searchData: q,
      })
      setItems(response.data?.items || [])
      setHasMore(false)
      setPage(1)
    } catch (error) {
      console.error('Error searching items:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const q = searchData.trim()
    if (q.length >= 4) {
      searchProductResult(q)
    }

    if (q.length === 0) {
      setPage(1)
      setHasMore(true)
      getAllItems(1)
    }
  }, [searchData])

  useEffect(() => {
    if (searchData.trim().length !== 0) return
    if (page === 1) return
    getAllItems(page)
  }, [page])

  const lastElementRef = useCallback(
    (node) => {
      if (loading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1)
        }
      })
      if (node) observer.current.observe(node)
    },
    [loading, hasMore]
  )

  const handleClearSearch = () => {
    setSearchData('')
  }

  const handleEditPrice = (id, price) => {
    setEditableRow(id)
    setEditedPrice(price)
  }

  const handleSavePrice = async (id) => {
    const item = items.find((item) => item._id === id)
    if (!item || item.price === parseFloat(editedPrice)) {
      setEditableRow(null)
      setEditedPrice('')
      return
    }

    try {
      await axios.put(`/api/v1/admin/bookstock/update-price`, {
        id,
        price: editedPrice,
      })
      setItems((prevItems) =>
        prevItems.map((item) =>
          item._id === id ? { ...item, price: parseFloat(editedPrice) } : item
        )
      )
      setEditableRow(null)
      setEditedPrice('')
    } catch (error) {
      console.error('Error updating price:', error)
    }
  }

  const handleKeyDown = (e, id) => {
    if (e.key === 'Enter') {
      handleSavePrice(id)
    } else if (e.key === 'Escape') {
      setEditableRow(null)
      setEditedPrice('')
    }
  }

  const handleAddBook = () => {
    setShowAddBookModal(true)
  }

  const handleCloseAddBookModal = () => {
    setShowAddBookModal(false)
  }

  const handleCategory = () => {
    setShowCategoryModal(true)
  }

  const handleCloseCategoryModal = () => {
    setShowCategoryModal(false)
  }

  const handleEditBook = (bookId) => {
    setEditBookData(bookId)
    setShowEditBookModal(true)
  }

  const handleCloseEditBookModal = () => {
    setShowEditBookModal(false)
    setEditBookData(null)
  }

  const handleUpdateBook = (updatedBook) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item._id === updatedBook._id ? { ...item, ...updatedBook } : item
      )
    )
  }

  const handleReturnHome = () => {
    navigate('/admin/dashboard')
  }

  const handleClickImport = () => {
    importFileRef.current?.click()
  }

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const ok = window.confirm('Confirm import?')
    if (!ok) {
      e.target.value = ''
      return
    }

    try {
      setImporting(true)

      const formData = new FormData()
      formData.append('file', file)

      await axios.post('/api/v1/admin/bookstock/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      toast.success('Imported successfully!')
      setSearchData('')
      setPage(1)
      setHasMore(true)

      await getAllItems(1)
    } catch (error) {
      console.error('Import failed:', error)
      toast.error(error.response?.data?.message || 'Import failed')
    } finally {
      setImporting(false)
      e.target.value = ''
    }
  }

  const importGuideText = `
Step1: Get stockitems from wms, and use this query, then Export to CSV: 
{
  isActive: "Checked",
  $or: [
    {
      itemGrp: { $nin: ["eBook", "MISC", "Stationa", "GBC", "BSM"] },
      itemType: { $nin: ["Outlines", "Nett", "Ministry Mag", "Min.Digest", "A&C", "CYP", "HWMR", "Edification"] }
    },
    {
      itemGrp: "Other La",
      itemType: "Nett"
    }
  ]
}

Step2: Import to gospel stockitems
Step3: Import by upload CSV file with below columns
        Excel format:
        Row 1 = header
        A = itemCode
        B = description
        C = price
        D = total
`

  const confirmToggleIsActive = async (item) => {
    const nextValue = item.isActive === 'Checked' ? 'Unchecked' : 'Checked'

    const ok = window.confirm('Confirm update?')
    if (!ok) return

    try {
      await axios.put('/api/v1/admin/bookstock/update-active', {
        id: item._id,
        isActive: nextValue,
      })

      setItems((prev) =>
        prev.map((x) =>
          x._id === item._id ? { ...x, isActive: nextValue } : x
        )
      )

      toast.success('Status updated')
    } catch (error) {
      console.log(error)
      toast.error('Failed to update status')
    }
  }

  return (
    <Layout title={'Book Stock Record'}>
      <div
        className='searchInventory shadow-sm p-3 bg-white rounded'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          height: '65px',
          position: 'fixed',
          zIndex: 1000,
        }}
      >
        <div className='search-bar' style={{ display: 'flex', width: '50%' }}>
          <input
            type='text'
            className='form-control'
            value={searchData}
            placeholder='Search Item Code / Book Name / Barcode'
            onChange={(e) => setSearchData(e.target.value)}
          />
          <button
            type='button'
            className='btn btn-secondary'
            disabled={!searchData}
            onClick={handleClearSearch}
          >
            Clear
          </button>
        </div>
        <div style={{ textAlign: 'center', fontWeight: 600 }}>
          Total Records: {totalRecords}
        </div>
        <div
          className='button-group'
          style={{
            display: 'flex',
            gap: '20px',
            alignItems: 'center',
          }}
        >
          <div className='d-flex align-items-center'>
            <button
              type='button'
              className='btn btn-warning btn-sm'
              onClick={handleClickImport}
              disabled={importing}
            >
              <i className='bi bi-upload'></i>{' '}
              {importing ? 'Importing...' : 'Import'}
            </button>

            <button
              type='button'
              className='btn btn-outline-warning btn-sm'
              onClick={() => setShowImportGuide(true)}
            >
              <i className='bi bi-info-circle'></i>
            </button>
          </div>

          <input
            type='file'
            ref={importFileRef}
            style={{ display: 'none' }}
            accept='.xlsx,.xls,.csv'
            onChange={handleImportFile}
          />

          <button
            type='button'
            className='btn btn-success btn-sm'
            onClick={handleAddBook}
          >
            <i className='bi bi-plus-circle'></i> Book
          </button>

          <button
            type='button'
            className='btn btn-info btn-sm'
            onClick={handleCategory}
          >
            Category
          </button>

          <button
            type='button'
            className='btn btn-secondary btn-sm'
            onClick={handleReturnHome}
          >
            <i className='bi bi-house-fill'></i>
          </button>
        </div>
      </div>
      <div>
        <div className='table-responsive'>
          <table className='table actable' style={{ marginTop: '80px' }}>
            <thead>
              <tr>
                <th>Item Code</th>
                <th>Book</th>
                <th className='text-end'>Price</th>
                <th>Category</th>
                <th>Barcode</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr
                  key={index}
                  ref={
                    index === items.length - 1 && hasMore && !searchData
                      ? lastElementRef
                      : null
                  }
                >
                  <td>{item.itemCode}</td>
                  <td>{item.description}</td>
                  <td
                    className='text-end'
                    onDoubleClick={() => handleEditPrice(item._id, item.price)}
                  >
                    {editableRow === item._id ? (
                      <input
                        type='number'
                        value={editedPrice}
                        onChange={(e) => setEditedPrice(e.target.value)}
                        onBlur={() => handleSavePrice(item._id)}
                        onKeyDown={(e) => handleKeyDown(e, item._id)}
                        autoFocus
                      />
                    ) : (
                      item.price?.toFixed(2)
                    )}
                  </td>
                  <td>{item.itemType}</td>
                  <td>{item.barCode}</td>
                  <td
                    className='text-center'
                    style={{ cursor: 'pointer' }}
                    onClick={() => confirmToggleIsActive(item)}
                  >
                    {item.isActive === 'Checked' ? (
                      <i
                        className='bi bi-check-circle-fill text-success'
                        title='Checked'
                      />
                    ) : (
                      <i
                        className='bi bi-x-circle-fill text-danger'
                        title='Unchecked'
                      />
                    )}
                  </td>

                  <td className='text-end'>
                    <button
                      type='button'
                      className='btn btn-primary btn-sm'
                      onClick={() => handleEditBook(item._id)}
                    >
                      <i className='bi bi-pencil-square'></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className='text-center'>{loading && <p>Loading items...</p>}</div>
      </div>

      <AdminAddBook show={showAddBookModal} onClose={handleCloseAddBookModal} />
      <AdminEditBook
        show={showEditBookModal}
        onClose={handleCloseEditBookModal}
        bookId={editBookData}
        onUpdateBook={handleUpdateBook}
      />
      <AdminBookCategory
        show={showCategoryModal}
        onClose={handleCloseCategoryModal}
      />

      <Modal
        show={showImportGuide}
        onHide={() => setShowImportGuide(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Import Guide</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <textarea
            className='form-control'
            value={importGuideText}
            readOnly
            rows={18}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowImportGuide(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  )
}

export default AdminBookStockList
